import type { FormEvent, ReactNode } from 'react'
import type { PageComponent } from '@/types'

import { useRouter } from 'next/router'
import { useState } from 'react'
import { cn } from '@/lib/utils'
import { Seo } from '@/components/generals/seo'
import { BrandMark } from '@/components/app/brand-mark'
import { controlClasses } from '@/components/ui/input'
import { useAcademic } from '@/components/providers/academic-provider'

import {
    ArrowRight,
    CircleAlert,
    Eye,
    EyeOff,
    LoaderCircle,
    Lock,
    UserRound
} from 'lucide-react'

const Login: PageComponent = (): ReactNode => {
    const router = useRouter()
    const { data } = useAcademic()
    const { settings } = data

    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()

        if (!username.trim() || !password) {
            setError('Username dan password wajib diisi.')

            return
        }

        setError(null)
        setSubmitting(true)
        window.setTimeout(() => void router.push('/'), 650)
    }

    return (
        <>
            <Seo title='Masuk' />

            <main className='relative grid min-h-screen place-items-center overflow-hidden bg-background px-4 py-10'>
                <div className='bg-grid pointer-events-none absolute inset-0 [mask-image:radial-gradient(ellipse_70%_60%_at_50%_0%,black,transparent)]' aria-hidden='true' />
                <div className='pointer-events-none absolute -top-40 -left-32 size-[28rem] rounded-full bg-primary/20 blur-3xl dark:bg-primary/12' aria-hidden='true' />
                <div className='pointer-events-none absolute -right-32 -bottom-40 size-[28rem] rounded-full bg-info/20 blur-3xl dark:bg-info/10' aria-hidden='true' />

                <div className='relative w-full max-w-[30rem]'>
                    <div className='rounded-3xl border bg-card/95 p-7 shadow-float backdrop-blur-xl sm:p-9 dark:bg-card/85'>
                        <div className='mb-7 flex items-center justify-center gap-3 text-center'>
                            {settings.showLogo && <BrandMark size={Math.max(44, settings.logoSize)} />}

                            <div className='min-w-0 text-left'>
                                <p className='truncate text-[17px] leading-tight font-extrabold tracking-tight text-foreground'>
                                    {settings.appName}
                                </p>

                                {settings.tagline && (
                                    <p className='mt-0.5 truncate text-[13px] text-muted-foreground'>
                                        {settings.tagline}
                                    </p>
                                )}
                            </div>
                        </div>

                        {error && (
                            <div className='mb-5 flex items-center gap-2.5 rounded-xl border border-destructive/25 bg-destructive/8 px-3.5 py-3 text-[13px] font-medium text-destructive' role='alert'>
                                <CircleAlert className='size-[18px] shrink-0' aria-hidden='true' />
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className='space-y-5' noValidate>
                            <div className='space-y-2'>
                                <label htmlFor='username' className='block text-[13.5px] font-bold text-foreground/90'>
                                    Username
                                </label>

                                <div className='group relative'>
                                    <UserRound className='pointer-events-none absolute top-1/2 left-4 size-[18px] -translate-y-1/2 text-muted-foreground transition-colors group-focus-within:text-primary' aria-hidden='true' />

                                    <input
                                        id='username'
                                        name='username'
                                        value={username}
                                        onChange={event => setUsername(event.target.value)}
                                        placeholder='Masukkan username'
                                        autoComplete='username'
                                        autoFocus
                                        required
                                        aria-invalid={error && !username.trim() ? true : undefined}
                                        className={cn(controlClasses, 'h-13 rounded-xl bg-muted/40 pr-4 pl-11 text-[15px] dark:bg-input/20')}
                                    />
                                </div>
                            </div>

                            <div className='space-y-2'>
                                <label htmlFor='password' className='block text-[13.5px] font-bold text-foreground/90'>
                                    Password
                                </label>

                                <div className='group relative'>
                                    <Lock className='pointer-events-none absolute top-1/2 left-4 size-[18px] -translate-y-1/2 text-muted-foreground transition-colors group-focus-within:text-primary' aria-hidden='true' />

                                    <input
                                        id='password'
                                        name='password'
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={event => setPassword(event.target.value)}
                                        placeholder='Masukkan password'
                                        autoComplete='current-password'
                                        required
                                        aria-invalid={error && !password ? true : undefined}
                                        className={cn(controlClasses, 'h-13 rounded-xl bg-muted/40 pr-14 pl-11 text-[15px] dark:bg-input/20')}
                                    />

                                    <button
                                        type='button'
                                        onClick={() => setShowPassword(value => !value)}
                                        className='absolute top-1/2 right-2.5 grid size-9 -translate-y-1/2 place-items-center rounded-lg border bg-card text-muted-foreground transition-colors outline-none hover:border-primary/40 hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/40'
                                        aria-label={showPassword ? 'Sembunyikan password' : 'Tampilkan password'}
                                    >
                                        {showPassword ? <EyeOff className='size-[18px]' aria-hidden='true' /> : <Eye className='size-[18px]' aria-hidden='true' />}
                                    </button>
                                </div>
                            </div>

                            <button
                                type='submit'
                                disabled={submitting}
                                className='flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary text-[15px] font-bold text-primary-foreground shadow-lg shadow-primary/25 transition-[background-color,transform,box-shadow] outline-none hover:-translate-y-px hover:bg-primary/92 hover:shadow-xl hover:shadow-primary/30 focus-visible:ring-3 focus-visible:ring-ring/40 active:translate-y-0 disabled:cursor-wait disabled:opacity-80 dark:shadow-none'
                            >
                                {submitting ? (
                                    <>
                                        <LoaderCircle className='size-[18px] animate-spin' aria-hidden='true' />
                                        Memeriksa akun...
                                    </>
                                ) : (
                                    <>
                                        Masuk ke Sistem
                                        <ArrowRight className='size-[18px]' aria-hidden='true' />
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            </main>
        </>
    )
}

Login.bare = true

export default Login
